import { useCallback, useEffect } from 'react';
import { useObjectState } from '../../../hooks/useObjectState';
import { useAxios } from '../../../hooks/useAxios';
import {
  GET_NOTIFICATION_DETAILS,
  MARK_NOTIFICATION_AS_ACKNOWLEDGED,
  NOTIFICATION_SUBMIT_ACTION,
} from '../../../api/api';
import { ACTIONS, remindMeOptions } from '../Constants';
import { useDispatch } from 'react-redux';
import { showNotification } from '../../../redux/notifications/actions';
import { useSelector } from 'react-redux';
import { notifications } from '../../../constants';

const intitialState = {
  isVisible: false,
  notificationData: [],
  activeAction: '',
  activeActionData: {},
  expandedNotifications: [],
};

const useNotificationController = () => {
  const currentBrand = useSelector((state) => state?.auth?.user?.brand);

  const [state, setState] = useObjectState(intitialState);
  //eslint-disable-next-line
  const dispatch = useDispatch();

  const onNotificationFetchSuccess = useCallback(
    (data) => {
      const newNotifications = Array.isArray(data) ? data : [];
      setState({ notificationData: newNotifications });
    },
    [setState]
  );

  const { callAxios: getNotifications } = useAxios({
    url: GET_NOTIFICATION_DETAILS,
    onSuccess: onNotificationFetchSuccess,
    callOnMount: false,
  });
  const { callAxios: markNotificationAsAcknowledged, isLoading: isMarkingAsViewed } = useAxios({
    method: 'POST',
    callOnMount: false,
    showError: true,
  });
  const { callAxios: remindMeLater, isLoading: isRemindingMeLater } = useAxios({
    url: NOTIFICATION_SUBMIT_ACTION,
    method: 'post',
    callOnMount: false,
    onSuccess: async (res) => {
      await getNotifications();
    },
    onError: (err) => {
      dispatch(showNotification(err || notifications.ERROR.something_wrong, 'error'));
    },
  });

  const toggleMessageReadMode = useCallback(
    (notificationId) => {
      let newExpandedNotifications = [...state.expandedNotifications];
      const isExpanded = newExpandedNotifications.find((id) => id === notificationId);

      if (isExpanded) {
        newExpandedNotifications = newExpandedNotifications.filter((id) => id !== notificationId);
      } else {
        newExpandedNotifications.push(notificationId);
      }

      setState({ expandedNotifications: newExpandedNotifications });
    },
    [setState, state.expandedNotifications]
  );

  const handleAction = useCallback(
    (actionType, actionData) => {
      setState({
        activeAction: actionType,
        activeActionData: actionData,
      });
    },
    [setState]
  );

  const remindMe = useCallback(
    async (value, notificationId, notificationMessageId) => {
      const timePeriod = remindMeOptions.find((option) => option.value === value)?.timePeriod;

      const payload = {
        notification_message: notificationMessageId,
        action: ACTIONS.REMIND_LATER,
        action_data: {
          time_period: timePeriod,
          number: value,
        },
      };

      remindMeLater({
        data: payload,
      });
    },
    [remindMeLater]
  );

  const setActiveAction = useCallback(
    (value) => {
      setState({ activeAction: value });
    },
    [setState]
  );

  const onCloseNotification = useCallback(
    async ({ notificationId, messageId, enableRefetchNotifications = true }) => {
      await markNotificationAsAcknowledged({
        url: MARK_NOTIFICATION_AS_ACKNOWLEDGED.replace(':notification_id', notificationId),
      }).then(async (res) => {
        // refetch notifications after marking current one as acknowledged
        if (enableRefetchNotifications) {
          await getNotifications();
        }
      });

      //request user feedback if there is messageId and record_feedback is present
      if (messageId) {
        const notificationData = state.notificationData?.find((notif) => notif?.id === notificationId) || {};

        setState({ activeAction: ACTIONS.RECORD_FEEDBACK, activeActionData: notificationData });
      }
    },
    [getNotifications, markNotificationAsAcknowledged, setState, state.notificationData]
  );

  useEffect(() => {
    if (currentBrand) {
      getNotifications();
    }

    return () => {
      setState({ notificationData: [] });
    };
    //eslint-disable-next-line
  }, [currentBrand]);

  const handlers = {
    handleAction,
    remindMe,
    onCloseNotification,
    setActiveAction,
    toggleMessageReadMode,
  };

  return [{ ...state, isMarkingAsViewed, isRemindingMeLater }, handlers];
};

export default useNotificationController;
